import { AccountSettings, CompanyInfo } from "01Data/Account";
import { AppContext } from "01Data/AppData";
import { PasswordPolicy } from "97Parts/Styles";
import { useMessageDialog } from "97Parts/useMessageDialog";
import {
  Backdrop,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useContext, useState } from "react";

const ErrorText = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  color: "#f00",
  margin: "0.2rem",
}));

type DialogResult = "ok" | "cancel";

type DialogProps = {
  open: boolean;
  onClose: (result: DialogResult) => void;
  accountSettings: AccountSettings;
};

const UpdateNameDialogContent: FC<DialogProps> = ({
  open,
  onClose,
  accountSettings,
}) => {
  const account = useContext(AppContext).account;
  const data = useContext(AppContext).dataUser;
  const [firstName, setFirstName] = useState(accountSettings.firstName);
  const [lastName, setLastName] = useState(accountSettings.lastName);
  const [phoneInput, setPhoneInput] = useState(
    accountSettings.companyPhone
      ? accountSettings.companyPhone.replace(/\+81/, "0")
      : ""
  );
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>({
    companyName: accountSettings.companyName,
    department: accountSettings.department,
    companyPhone: accountSettings.companyPhone,
  });
  const [errorMessage, setErrorMessage] = useState("");
  const { MessageDialog, openMessageDialog } = useMessageDialog();
  const [loading, setLoading] = useState(false);

  const handleApply = async () => {
    if (account.IsUserAdmin() && companyInfo.companyName === "") {
      setErrorMessage("法人名を入力してください。");
      return;
    }
    if (lastName === "") {
      setErrorMessage(
        (account.IsUserAdmin() ? "担当者 姓" : "姓") + `を入力してください。`
      );
      return;
    }
    if (firstName === "") {
      setErrorMessage(
        (account.IsUserAdmin() ? "担当者 名" : "名") + `を入力してください。`
      );
      return;
    }
    if (account.IsUserAdmin() && companyInfo.department === "") {
      setErrorMessage("部署名を入力してください。");
      return;
    }
    if (account.IsUserAdmin() && phoneInput === "") {
      setErrorMessage("電話番号を入力してください。");
      return;
    }
    if (account.IsUserAdmin() && phoneInput.match(/^0[0-9]{9,10}$/) === null) {
      // 0はじまり、10文字 or 11文字の場合パス
      setErrorMessage("有効な電話番号を入力してください。");
      return;
    }
    try {
      setLoading(true);
      await account.ChangeNames(lastName, firstName);
      if (account.IsUserAdmin()) {
        // TODO put/users
        data.process.edit.CompanyName = companyInfo.companyName;
        data.process.edit.DepartmentName = companyInfo.department;
        data.process.edit.PhoneNumber = phoneInput.replace(/^0/, "+81");
        data.process.edit.UserId = await account.GetLoginUserId();
        await data.process.EditUser();
      }
      await openMessageDialog();
      window.location.reload();
      // onClose("ok");
    } catch (error) {
      setErrorMessage(`更新に失敗しました。\n${(error as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (event: {}, reason: "backdropClick") => {
    if (reason !== "backdropClick") {
      onClose("cancel");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="update-name-dialog-title"
    >
      <DialogTitle id="update-name-dialog-title">
        {account.IsUserAdmin() ? "ユーザー情報変更" : "ユーザー名変更"}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus={account.IsUserAdmin() ? true : false}
          required
          fullWidth
          type="text"
          variant="filled"
          margin="normal"
          id="companyName"
          label="法人名"
          name="companyName"
          onChange={(e) => {
            setErrorMessage("");
            setCompanyInfo({ ...companyInfo, companyName: e.target.value });
          }}
          inputProps={{ maxLength: 128 }}
          value={companyInfo.companyName}
          sx={{ display: account.IsUserAdmin() ? "box" : "none" }}
        />
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <TextField
            autoFocus={account.IsUserAdmin() ? false : true}
            required
            sx={{ width: "48%" }}
            type="text"
            variant="filled"
            margin="normal"
            id="lastName"
            label={account.IsUserAdmin() ? "担当者 姓" : "姓"}
            name="lastName"
            onChange={(e) => {
              setErrorMessage("");
              setLastName(e.target.value);
            }}
            inputProps={{ maxLength: 128 }}
            value={lastName}
          />
          <TextField
            required
            sx={{ width: "48%" }}
            type="text"
            variant="filled"
            margin="normal"
            id="firstName"
            label={account.IsUserAdmin() ? "担当者 名" : "名"}
            name="firstName"
            onChange={(e) => {
              setErrorMessage("");
              setFirstName(e.target.value);
            }}
            inputProps={{ maxLength: 128 }}
            value={firstName}
          />
        </div>
        <TextField
          required
          fullWidth
          type="text"
          variant="filled"
          margin="normal"
          id="department"
          label="部署名"
          name="department"
          onChange={(e) => {
            setErrorMessage("");
            setCompanyInfo({ ...companyInfo, department: e.target.value });
          }}
          inputProps={{ maxLength: 128 }}
          value={companyInfo.department}
          sx={{ display: account.IsUserAdmin() ? "box" : "none" }}
        />
        <TextField
          required
          fullWidth
          variant="filled"
          margin="normal"
          id="companyPhone"
          name="companyPhone"
          label="電話番号"
          type="tel"
          autoComplete="tel"
          inputProps={{ maxLength: 11 }}
          onChange={(e) => {
            setErrorMessage("");
            setPhoneInput(e.target.value);
          }}
          value={phoneInput}
          sx={{ display: account.IsUserAdmin() ? "box" : "none" }}
        />
        <PasswordPolicy
          sx={{ display: account.IsUserAdmin() ? "block" : "none" }}
        >
          ※電話番号は数字で入力してください。
        </PasswordPolicy>

        <ErrorText>{errorMessage ? errorMessage : "　"}</ErrorText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleApply}>更新する</Button>
        <Button onClick={() => onClose("cancel")}>ユーザー設定に戻る</Button>
      </DialogActions>
      <MessageDialog
        message={
          (account.IsUserAdmin() ? "ユーザー情報" : "ユーザー名") +
          "を更新しました。変更を反映するため、ページリロードを行います。"
        }
      />
      <Backdrop open={loading} sx={{ zIndex: 1201 }}>
        <CircularProgress sx={{ color: "white" }} />
      </Backdrop>
    </Dialog>
  );
};

export const useUpdateNameDialog = () => {
  const [open, setOpen] = useState(false);
  const [resolve, setResolve] = useState<(value: DialogResult) => void>();

  const openUpdateNameDialog = () => {
    setOpen(true);
    return new Promise<DialogResult>((resolve) => {
      setResolve(() => resolve);
    });
  };
  const onClose = (result: DialogResult) => {
    setOpen(false);
    if (resolve) {
      resolve(result);
    }
  };

  const UpdateNameDialog: FC<{ account: AccountSettings }> = (props) => (
    <UpdateNameDialogContent
      open={open}
      onClose={onClose}
      accountSettings={props.account}
    />
  );

  return {
    UpdateNameDialog,
    openUpdateNameDialog,
  };
};
